import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { OperatorApp } from '@/features/operator-selection/types/operator-types';
import { servicesContainer } from '@/core/di/services-container';

interface OperatorStore {
  operators: OperatorApp[];
  selectedOperator: OperatorApp | null;
  error: string | null;
  isLoading: boolean;
  seenOperatorSelector: boolean;
  lastDistributorId: string | null;
  loadOperators: (distributorId?: string) => Promise<void>;
  selectOperator: (operator: OperatorApp, distributorId?: string | null) => void;
  clearSelection: () => void;
  markOperatorSelectorAsSeen: (distributorId?: string | null) => void;
  checkDistributorChange: (currentDistributorId: string | null) => boolean;
  resetForDistributorChange: () => void;
}

export const useOperatorStore = create<OperatorStore>()(
  persist(
    (set, get) => ({
      operators: [],
      selectedOperator: null,
      error: null,
      isLoading: false,
      seenOperatorSelector: false,
      lastDistributorId: null,

      loadOperators: async (distributorId?: string) => {
        if (get().isLoading) {
          return;
        }
        
        set({ isLoading: true, error: null });
        try {
          const services = servicesContainer.getServices();
          const response = await services.operatorApi.getOperatorApps(distributorId);
          const operators = response.rows || [];

          const currentSelected = get().selectedOperator;
          const refreshedSelected = currentSelected
            ? (operators.find((o) => o.appId === currentSelected.appId) ?? currentSelected)
            : null;

          set({ operators, isLoading: false, selectedOperator: refreshedSelected });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load operators',
          });
        }
      },

      selectOperator: (operator: OperatorApp, distributorId?: string | null) => {
        // Set state synchronously - distributorId should be passed from the component
        // which has access to auth store's distributorId
        set({
          selectedOperator: operator,
          error: null,
          seenOperatorSelector: true,
          lastDistributorId: distributorId ?? null
        });
      },

      clearSelection: () => {
        set({ 
          selectedOperator: null, 
          seenOperatorSelector: false,
          lastDistributorId: null 
        });
      },

      markOperatorSelectorAsSeen: (distributorId?: string | null) => {
        set({
          seenOperatorSelector: true,
          lastDistributorId: distributorId ?? null
        });
      },
      
      checkDistributorChange: (currentDistributorId: string | null) => {
        const state = get();

        // If we haven't seen the selector yet, no change to detect
        if (!state.seenOperatorSelector) {
          return false;
        }

        // If we have an operator selected, no need to show selector again
        // This prevents the selector from appearing on every navigation in iframe mode
        if (state.selectedOperator) {
          return false;
        }

        // Only detect distributor change if both values are non-null
        // This prevents false positives when lastDistributorId was never set
        if (!state.lastDistributorId || !currentDistributorId) {
          return false;
        }

        // Check if the distributor has changed since last operator selection
        return state.lastDistributorId !== currentDistributorId;
      },
      
      resetForDistributorChange: () => {
        // This method is no longer needed since we handle the logic directly in ProtectedRoute
        // But keeping it for potential future use
        set({ 
          seenOperatorSelector: false,
          lastDistributorId: null,
          // Clear operators list to force reload with new distributor context
          operators: []
        });
      },
    }),
    {
      name: 'operator-storage',
      partialize: (state) => ({
        selectedOperator: state.selectedOperator,
        seenOperatorSelector: state.seenOperatorSelector,
        lastDistributorId: state.lastDistributorId,
      }),
      onRehydrateStorage: () => () => {
        // Rehydration complete
      }
    }
  )
);
